/**
 * Cart Drawer
 *
 * Server-rendered inside the overlay section group.
 * Uses event delegation so all listeners survive DOM refreshes.
 * Section Rendering API (`?sections=`) keeps the drawer in sync.
 */

/* ── helpers ─────────────────────────────────────────────────── */

/**
 * All cart-rendering sections currently on the page.
 * On most pages that is just the drawer; on /cart it is the drawer *and* the
 * cart page section, so both stay in sync after every mutation.
 */
function getCartSectionIds() {
  var ids = [];
  document.querySelectorAll('[data-cart-section-id]').forEach(function (el) {
    var id = el.dataset.cartSectionId;
    if (id && ids.indexOf(id) === -1) ids.push(id);
  });
  return ids;
}

function getCartSectionId() {
  return getCartSectionIds()[0] || null;
}

/** True when the shopper is already on the cart page. */
function isCartPage() {
  return !!document.querySelector('.section-cart-page');
}

function updateCartCount() {
  fetchCartJson()
    .then(function (data) {
      document.querySelectorAll('.cart-count').forEach(function (el) {
        el.innerHTML = data.item_count > 0 ? data.item_count : '';
        el.hidden = data.item_count <= 0;
      });
    })
    .catch(console.error);
}

/* ── drawer open / close with focus‑trap (WCAG) ──────────────── */

var _previouslyFocused = null;

function setCartTriggersExpanded(isOpen) {
  var triggers = document.querySelectorAll(
    '.js-cart-toggle[aria-expanded], .header-cart[aria-expanded], .header-cell--cart[aria-expanded]'
  );
  Array.prototype.forEach.call(triggers, function (trigger) {
    trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  });
}

function openCartDrawer() {
  var drawer = document.querySelector('.cart-sidebar');
  if (!drawer) return;

  _previouslyFocused = document.activeElement;

  if (window.closeOtherDrawers) window.closeOtherDrawers(['open-drawer']);
  if (window.smUnparkPanel) window.smUnparkPanel(drawer);
  document.body.classList.add('open-drawer');
  if (window.smLockBodyScroll) window.smLockBodyScroll();

  drawer.setAttribute('aria-hidden', 'false');
  setCartTriggersExpanded(true);

  setTimeout(function () { drawer.focus(); }, 50);
}

function closeCartDrawer(restoreFocus) {
  var drawer = document.querySelector('.cart-sidebar');
  if (!drawer) return;

  if (!document.body.classList.contains('open-drawer') &&
      !document.body.classList.contains('open-cart')) return;

  document.body.classList.remove('open-drawer', 'open-cart');
  if (window.smUnlockBodyScroll) window.smUnlockBodyScroll();
  if (window.smParkPanel) window.smParkPanel(drawer);
  drawer.setAttribute('aria-hidden', 'true');
  setCartTriggersExpanded(false);

  if (restoreFocus !== false && _previouslyFocused && _previouslyFocused.focus) {
    _previouslyFocused.focus();
  }
}

/* cart-drawer.js loads before theme.js (see theme.liquid), so registration
   is deferred to DOMContentLoaded — every deferred script, theme.js
   included, has already run its top-level code by the time that fires. */
document.addEventListener('DOMContentLoaded', function () {
  if (window.registerDrawerCloser) {
    window.registerDrawerCloser('open-drawer', function () { closeCartDrawer(false); });
  }
});

function toggleCartDrawer() {
  if (document.body.classList.contains('open-drawer') || document.body.classList.contains('open-cart')) {
    closeCartDrawer();
  } else {
    openCartDrawer();
  }
}

/* ── auto-added products (the theme half of a native Shopify discount) ──
   snippets/cart-free-gift renders one verdict per "Free Gift / Auto-add" block
   on the Cart Drawer — does the cart meet that campaign's condition, is its
   product already in, did Shopify's discount actually price it — plus the keys
   of any orphaned lines left behind by a campaign that has since been deleted
   or switched off. This side only executes those verdicts. Shopify never adds
   the rewarded product itself, which is the whole reason this exists. */

/* One render to settle a change, one more to verify the discounts landed. */
var FREE_GIFT_MAX_PASSES = 2;

/* Cart fingerprint each campaign's discount check last failed on — the discount
   is paused, expired, or its conditions no longer match the block's. Without it
   the next sync would add the product straight back and loop. Keyed on the cart
   rather than latched for the page view, so changing the cart gets another try:
   the two thresholds may simply be one item apart. */
var _freeGiftFailures = {};
var _freeGiftSyncing = false;

function getFreeGiftConfig() {
  var el = document.getElementById('cart-free-gift-config');
  if (!el) return null;
  try {
    return JSON.parse(el.textContent);
  } catch (e) {
    console.error('Free gift: could not read config', e);
    return null;
  }
}

function changeFreeGiftLine(key, quantity) {
  return fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    /* `id` with a line item key targets exactly one line — unlike `line`, it
       stays valid while the other lines in this same pass are changed. */
    body: JSON.stringify({ id: key, quantity: quantity })
  }).then(function (r) {
    if (!r.ok) throw new Error('Cart change failed');
    return r.json();
  });
}

function addFreeGiftLines(items) {
  return fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ items: items })
  }).then(function (r) {
    if (!r.ok) throw new Error('Free gift add failed');
    return r.json();
  });
}

/**
 * Brings the cart in line with every campaign's verdict at once.
 * Resolves to true when the cart was changed (the caller then re-renders).
 */
function syncFreeGift() {
  var cfg = getFreeGiftConfig();
  if (!cfg || _freeGiftSyncing) return Promise.resolve(false);

  var removals = (cfg.orphans || []).slice();
  var quantityChanges = [];
  var additions = [];

  (cfg.rules || []).forEach(function (rule) {
    /* An inactive campaign's line is already in cfg.orphans. */
    if (!rule.active) return;

    var discountFailedHere = _freeGiftFailures[rule.id] === rule.signature;
    var wanted = rule.qualifies && rule.available && rule.variant_id > 0 && !discountFailedHere;

    if (!wanted) {
      /* Condition no longer met (or the product sold out) — take it back out. */
      if (rule.in_cart) removals.push(rule.line_key);
      return;
    }

    if (!rule.in_cart) {
      /* Underscore-prefixed: hidden from the shopper, still readable in Liquid
         and on the order. The campaign ID is what ties the line to its block. */
      var properties = { _free_gift: rule.id };
      if (rule.label) properties._free_gift_label = rule.label;
      additions.push({ id: rule.variant_id, quantity: rule.quantity, properties: properties });
      return;
    }

    if (rule.line_variant_id !== rule.variant_id) {
      /* Merchant swapped the product — drop the stale line, the next pass adds
         the new one. */
      removals.push(rule.line_key);
      return;
    }

    if (rule.line_quantity !== rule.quantity) {
      quantityChanges.push({ key: rule.line_key, quantity: rule.quantity });
      return;
    }

    if (rule.require_discount && !rule.discount_ok) {
      /* Sitting in the cart at full price: nobody should be charged for it. */
      _freeGiftFailures[rule.id] = rule.signature;
      removals.push(rule.line_key);
    }
  });

  if (!removals.length && !quantityChanges.length && !additions.length) {
    return Promise.resolve(false);
  }

  /* Line changes go one at a time (the cart API takes a single line per call),
     every addition rides in one request. Removals lead, so a swapped product
     never briefly sits in the cart twice. */
  _freeGiftSyncing = true;
  var chain = Promise.resolve();
  removals.forEach(function (key) {
    chain = chain.then(function () { return changeFreeGiftLine(key, 0); });
  });
  quantityChanges.forEach(function (change) {
    chain = chain.then(function () { return changeFreeGiftLine(change.key, change.quantity); });
  });
  if (additions.length) {
    chain = chain.then(function () { return addFreeGiftLines(additions); });
  }

  return chain
    .then(function () { return true; })
    .catch(function (error) {
      console.error('Free gift sync failed:', error);
      return false;
    })
    .finally(function () { _freeGiftSyncing = false; });
}

/* ── core: refresh cart drawer via Section Rendering API ───── */

/**
 * `depth` guards the free-gift sync loop: a sync that changed the cart needs
 * another render to settle (and, for the discount check, one more to verify the
 * freshly added gift really got discounted), but never more than that.
 */
function refreshCart(depth) {
  var pass = typeof depth === 'number' ? depth : 0;

  var sectionIds = getCartSectionIds();
  if (!sectionIds.length) return Promise.resolve();

  /* Sections must be requested in the context of the current template: the cart
     page section only exists on /cart, while the drawer exists everywhere. */
  var base = window.location.pathname || (window.Shopify && window.Shopify.routes.root) || '/';

  return fetch(base + '?sections=' + sectionIds.join(','))
    .then(function (response) { return response.json(); })
    .then(function (data) {
      sectionIds.forEach(function (sectionId) {
        if (!data[sectionId]) return;

        var wrapper = document.getElementById('shopify-section-' + sectionId);
        if (!wrapper) return;

        var parsed = new DOMParser().parseFromString(data[sectionId], 'text/html');
        var fresh  = parsed.getElementById('shopify-section-' + sectionId);
        if (fresh) {
          wrapper.innerHTML = fresh.innerHTML;
        }
      });

      /* keep drawer visibly open after refresh */
      var newDrawer = document.querySelector('.cart-sidebar');
      if (document.body.classList.contains('open-drawer')) {
        if (newDrawer) {
          newDrawer.setAttribute('aria-hidden', 'false');

          if (document.activeElement === document.body || !newDrawer.contains(document.activeElement)) {
            newDrawer.focus();
          }
        }
      } else if (newDrawer && window.smParkPanel) {
        window.smParkPanel(newDrawer, true);
      }

      updateCartCount();
      initSplide();
      initCompleteBagSplide();
      loadCartRecommendations();
      fillPendingMemberDiscount();

      /* The just-rendered sections carry a fresh free-gift verdict; act on it. */
      if (pass >= FREE_GIFT_MAX_PASSES) return;
      return syncFreeGift().then(function (changed) {
        if (changed) return refreshCart(pass + 1);
      });
    })
    .catch(function (error) {
      console.error('Error refreshing cart:', error);
    });
}

/** Backward-compatible alias – theme.js calls load_cart() */
function load_cart() {
  return refreshCart();
}

/* ── Splide init (scoped to cart drawer) ─────────────────────── */

function initSplide() {
  var sectionId = getCartSectionId();
  if (!sectionId || typeof Splide === 'undefined') return;

  var el = document.getElementById('splide-' + sectionId);
  if (!el) return;

  /* avoid double-init */
  if (el.classList.contains('is-initialized')) return;

  new Splide('#splide-' + sectionId, {
    type: 'slide',
    perPage: 2,
    gap: '0.75rem',
    pagination: false,
    arrows: true,
    breakpoints: {
      600: { perPage: 1 }
    },
    i18n: {
      prev: 'Previous upsell product',
      next: 'Next upsell product',
      slide: 'Go to upsell product %s'
    }
  }).mount();
}

/* ── "Complete your bag" — product-card + Splide (default, no manual override) ──
   Renders products fetched from Shopify's recommendations endpoint through the
   theme's existing product-card snippet (via the same section that already uses
   it), then mounts them into the cart drawer's own Splide slider below. */

function loadCartRecommendations() {
  document.querySelectorAll('.js-cart-recommendations').forEach(loadCartRecommendationsFor);
}

function loadCartRecommendationsFor(container) {
  var productId = container.dataset.baseProductId;
  if (!productId) return;

  /* Cart Drawer instances render its own recommendation section (its own
     product card design); every other instance (e.g. the Cart Page) keeps
     fetching the shared product-recommendation section unchanged. */
  var isDrawerCards = container.dataset.cardStyle === 'drawer';
  var sectionId = isDrawerCards ? 'cart-drawer-recommendation' : 'product-recommendation';
  var itemSelector = isDrawerCards ? '.cart-drawer-recommendation__item' : '.product-recommendation__item';

  var limit = container.dataset.limit || '4';
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var url = root + 'recommendations/products?section_id=' + sectionId + '&product_id=' + productId + '&limit=10&intent=related';

  fetch(url)
    .then(function (r) { return r.text(); })
    .then(function (html) {
      var parsed = new DOMParser().parseFromString(html, 'text/html');
      var rawItems = parsed.querySelectorAll(itemSelector);
      if (!rawItems.length) return;

      var items = Array.from(rawItems).slice(0, parseInt(limit, 10));

      var splide = document.createElement('div');
      splide.className = 'splide js-cart-complete-bag-splide cart-complete-bag__splide';
      /* carry the block's slides-per-view through to the generated slider */
      if (container.dataset.perPage) splide.dataset.perPage = container.dataset.perPage;
      if (container.dataset.perPageMobile) splide.dataset.perPageMobile = container.dataset.perPageMobile;

      var track = document.createElement('div');
      track.className = 'splide__track';

      var list = document.createElement('ul');
      list.className = 'splide__list';

      items.forEach(function (item) {
        var li = document.createElement('li');
        li.className = 'splide__slide';
        li.innerHTML = item.innerHTML;
        list.appendChild(li);
      });

      track.appendChild(list);
      splide.appendChild(track);

      container.innerHTML = '';
      container.appendChild(splide);

      initCompleteBagSplide();
      if (typeof initProductCardSplides === 'function') initProductCardSplides(container);
    })
    .catch(function (error) {
      console.error('Error loading cart recommendations:', error);
    });
}

/* ── Cart Drawer upsell card — shade swatches ──────────────── */

function getDrawerCardVariants(card) {
  if (card._drawerCardVariants) return card._drawerCardVariants;

  var node = card.querySelector('.js-drawer-card-variant-data');
  var data = [];
  if (node) {
    try {
      data = JSON.parse(node.textContent) || [];
    } catch (error) {
      console.error('Cart drawer card variant data parse error:', error);
    }
  }

  card._drawerCardVariants = data;
  return data;
}

function updateDrawerCardVariant(card, variantId) {
  var variants = getDrawerCardVariants(card);
  var variant = null;

  for (var i = 0; i < variants.length; i++) {
    if (String(variants[i].id) === String(variantId)) {
      variant = variants[i];
      break;
    }
  }
  if (!variant) return;

  card.dataset.activeVariantId = variant.id;

  card.querySelectorAll('.js-drawer-shade-dot').forEach(function (dot) {
    var isActive = String(dot.dataset.variantId) === String(variant.id);
    dot.classList.toggle('is-active', isActive);
    dot.setAttribute('aria-pressed', isActive ? 'true' : 'false');
  });

  var img = card.querySelector('.js-drawer-card-image');
  if (img && variant.image) {
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.src = variant.image;
  }

  if (variant.url) {
    var imageLink = card.querySelector('.cart-drawer-product-card__image-link');
    var titleLink = card.querySelector('.cart-drawer-product-card__title a');
    if (imageLink) imageLink.href = variant.url;
    if (titleLink) titleLink.href = variant.url;
  }

  var priceEl = card.querySelector('.js-drawer-card-price');
  if (priceEl && variant.price) priceEl.innerHTML = variant.price;

  var subtitleEl = card.querySelector('.js-drawer-card-subtitle');
  if (subtitleEl) {
    subtitleEl.textContent = variant.subtitle || '';
    subtitleEl.hidden = !variant.subtitle;
  }

  var addBtn = card.querySelector('.js-upsell-add-to-cart');
  if (addBtn) {
    addBtn.dataset.variantId = variant.id;
    addBtn.disabled = !variant.available;
    var label = variant.available ? addBtn.dataset.labelAdd : addBtn.dataset.labelSoldOut;
    if (label) addBtn.textContent = label;
  }
}

/* ── "Complete your bag" Splide — desktop/drawer arrows live in the block
   header (.cart-complete-bag__arrows); the Cart Page additionally has a
   mobile-only controls row (progress bar + Prev/Next, .cart-complete-bag__controls)
   mirroring Featured Products Row's mobile pattern (assets/featured-products-row.js).
   Both arrow pairs drive the same slider — querySelectorAll below just means
   whichever pair is visible works, the other stays inert and hidden. ── */

function initCompleteBagSplide() {
  if (typeof Splide === 'undefined') return;

  document.querySelectorAll('.js-cart-complete-bag-splide').forEach(function (el) {
    if (el.classList.contains('is-initialized')) return;

    var wrapper = el.closest('.cart-complete-bag');
    var prevBtns = wrapper ? wrapper.querySelectorAll('.cart-complete-bag__arrow-btn--prev') : [];
    var nextBtns = wrapper ? wrapper.querySelectorAll('.cart-complete-bag__arrow-btn--next') : [];
    var progressBar = wrapper ? wrapper.querySelector('.js-cart-complete-bag-progress-bar') : null;

    /* Slides per view comes from the block (cart drawer: 3 — cart page: 4 / 2 on mobile) */
    var perPage = parseInt(el.dataset.perPage, 10) || 3;
    var perPageMobile = parseInt(el.dataset.perPageMobile, 10) || perPage;

    /* Only the Cart Page's mobile slider gets the Featured-Products-Row-style
       16px left/right peek padding — the Cart Drawer's version is untouched. */
    var isCartPage = !!(wrapper && wrapper.closest('.cart-page__completed-your-bag-inner, .cart-page'));
    var isDrawerCards = !!(wrapper && wrapper.querySelector('.cart-drawer-product-card'));

    var mobileBreakpoint;
    if (isDrawerCards) {
      /* Cart Drawer mobile: 2.5 products visible, same fixedWidth technique
         (perPage/perMove stay integers, the slide width does the rest) used
         by Featured Products Row (assets/featured-products-row.js). */
      var mobileGap = '10px';
      var mobilePerView = 2.6;
      var mobileSlideWidth = 'calc((100% + ' + mobileGap + ')/' + mobilePerView + ' - ' + mobileGap + ')';
      mobileBreakpoint = {
        perPage: 1,
        perMove: 1,
        gap: mobileGap,
        fixedWidth: mobileSlideWidth,
        drag: true
      };
    } else {
      mobileBreakpoint = { perPage: perPageMobile };
      if (isCartPage) {
        mobileBreakpoint.padding = { left: '16px', right: '16px' };
      }
    }

    var slider = new Splide(el, {
      type: 'slide',
      perPage: perPage,
      perMove: 1,
      gap: isCartPage ? '16px' : '10px',
      pagination: false,
      arrows: false,
      drag: true,
      breakpoints: {
        991: mobileBreakpoint
      },
      i18n: {
        prev: 'Previous product',
        next: 'Next product'
      }
    });

    prevBtns.forEach(function (prevBtn) {
      prevBtn.addEventListener('click', function () {
        if (!prevBtn.classList.contains('is-disabled')) slider.go('<');
      });
    });
    nextBtns.forEach(function (nextBtn) {
      nextBtn.addEventListener('click', function () {
        if (!nextBtn.classList.contains('is-disabled')) slider.go('>');
      });
    });

    function syncControls() {
      var end = slider.Components.Controller.getEnd();
      if (!isFinite(end) || end < 0) end = 0;
      var index = Math.min(Math.max(slider.index, 0), end);

      prevBtns.forEach(function (prevBtn) {
        var prevDisabled = index <= 0;
        prevBtn.disabled = prevDisabled;
        prevBtn.classList.toggle('is-disabled', prevDisabled);
      });
      nextBtns.forEach(function (nextBtn) {
        var nextDisabled = index >= end;
        nextBtn.disabled = nextDisabled;
        nextBtn.classList.toggle('is-disabled', nextDisabled);
      });
      if (progressBar) {
        var rate = isCartPage
          ? Math.min((index + 1) / (end + 1), 1)
          : Math.min((index + 2) / (end + 1), 1);
        progressBar.style.width = String(100 * rate) + '%';
      }
    }

    slider.on('mounted move moved updated resized', syncControls);
    slider.mount();
    el.classList.add('is-initialized');
  });
}

/* ── cart mutations ───────────────────────────────────────────── */

function updateCartItemQuantity(line, quantity) {
  fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ line: line, quantity: quantity })
  })
    .then(function (r) { return r.json(); })
    .then(function () { refreshCart(); })
    .catch(function (e) { console.error('Error updating cart:', e); });
}

/* ── discount helpers ────────────────────────────────────────── */

/* Two /cart.js reads happen back to back while applying a code, and the browser
   will happily serve the second one from its HTTP cache — i.e. the cart as it was
   *before* the discount, which is what made a perfectly valid code report INVALID
   until the shopper refreshed. Bust the cache on every read. */
function fetchCartJson() {
  return fetch('/cart.js?t=' + Date.now(), {
    method: 'GET',
    cache: 'no-store',
    headers: { 'Accept': 'application/json' }
  }).then(function (r) { return r.json(); });
}

function discountCodesFromCart(data) {
  var codes = [];
  if (data && data.cart_level_discount_applications) {
    data.cart_level_discount_applications.forEach(function (d) {
      if (d.type === 'discount_code') codes.push(d.title);
    });
  }
  if (data && data.items) {
    data.items.forEach(function (item) {
      if (item.line_level_discount_allocations && item.line_level_discount_allocations.length) {
        item.line_level_discount_allocations.forEach(function (d) {
          if (d.discount_application && d.discount_application.type === 'discount_code') {
            codes.push(d.discount_application.title);
          }
        });
      }
    });
  }
  return codes.filter(function (v, i, a) { return a.indexOf(v) === i; });
}

function fetchCurrentDiscountCodes() {
  return fetchCartJson().then(discountCodesFromCart);
}

/* `root` scopes the lookup to one .cart-discount instance — on /cart both the
   drawer and the cart page render the snippet, so an unscoped query would hit
   the wrong one. */
function showDiscountInvalid(root) {
  var status = (root || document).querySelector('.js-discount-status');
  if (status) {
    /* Text comes from the server-rendered translation (sections.cart_drawer.discount_invalid) */
    status.hidden = false;
  }
}

/* `root` scopes the lookup to one .cart-discount instance, same reason as showDiscountInvalid. */
function setDiscountApplyLoading(root, isLoading) {
  var scope = root || document;
  var applyBtn = scope.querySelector('.js-discount-apply-link');
  var loaderEl = scope.querySelector('.js-discount-loader');
  if (applyBtn) applyBtn.disabled = isLoading;
  if (loaderEl) loaderEl.hidden = !isLoading;
}

/* Case-insensitively: did `code` end up on the cart? Retries because the cart is
   recalculated server-side a beat after /checkout?discount= returns. */
var DISCOUNT_VERIFY_ATTEMPTS = 4;
var DISCOUNT_VERIFY_DELAY = 350;

function verifyDiscountApplied(code, attemptsLeft) {
  return fetchCurrentDiscountCodes().then(function (applied) {
    var found = applied.some(function (c) {
      return c.toLowerCase() === code.toLowerCase();
    });
    if (found) return true;
    if (attemptsLeft <= 1) return false;
    return new Promise(function (resolve) {
      setTimeout(resolve, DISCOUNT_VERIFY_DELAY);
    }).then(function () {
      return verifyDiscountApplied(code, attemptsLeft - 1);
    });
  });
}

function applyDiscountCode(discountCode, root) {
  setDiscountApplyLoading(root, true);

  fetchCurrentDiscountCodes().then(function (codes) {
    var alreadyApplied = codes.some(function (c) {
      return c.toLowerCase() === discountCode.toLowerCase();
    });
    if (alreadyApplied) {
      showDiscountInvalid(root);
      setDiscountApplyLoading(root, false);
      return;
    }
    codes.push(discountCode);

    fetch('/checkout?discount=' + codes.join(','), { method: 'GET' })
      .finally(function () {
        /* Shopify needs a moment to recalculate the cart after /checkout?discount=,
           so a single read can miss a code that did land. Poll a few times before
           calling it invalid — matching what the shopper sees after a refresh. */
        verifyDiscountApplied(discountCode, DISCOUNT_VERIFY_ATTEMPTS)
          .then(function (wasApplied) {
            if (wasApplied) {
              /* Section re-render resets the discount snippet back to its idle state,
                 which also discards the loading button along with it. */
              refreshCart();
            } else {
              showDiscountInvalid(root);
              setDiscountApplyLoading(root, false);
            }
          })
          .catch(function (error) {
            console.error('Error applying discount code:', error);
            showDiscountInvalid(root);
            setDiscountApplyLoading(root, false);
          });
      });
  }).catch(function (error) {
    console.error('Error applying discount code:', error);
    setDiscountApplyLoading(root, false);
  });
}

function removeDiscountCode(code) {
  fetchCurrentDiscountCodes().then(function (codes) {
    var remaining = codes.filter(function (c) { return c !== code; });
    fetch('/checkout?discount=' + remaining.join(','), { method: 'GET' })
      .finally(function () { refreshCart(); });
  });
}

var MEMBER_DISCOUNT_STORAGE_KEY = 'sm-member-discount-code';
var MEMBER_DISCOUNT_COPIED_DURATION = 2000;

function getPendingMemberDiscount() {
  try {
    return sessionStorage.getItem(MEMBER_DISCOUNT_STORAGE_KEY) || '';
  } catch (e) {
    return '';
  }
}

function setPendingMemberDiscount(code) {
  try {
    if (code) {
      sessionStorage.setItem(MEMBER_DISCOUNT_STORAGE_KEY, code);
    } else {
      sessionStorage.removeItem(MEMBER_DISCOUNT_STORAGE_KEY);
    }
  } catch (e) {}
}

function isDiscountCodeShownAsApplied(code) {
  return Array.prototype.some.call(document.querySelectorAll('.cart-discount__applied-code'), function (el) {
    return el.textContent.trim().toLowerCase() === code.toLowerCase();
  });
}

function isMemberDiscountOffered(code) {
  return Array.prototype.some.call(document.querySelectorAll('.js-member-discount-copy'), function (el) {
    return el.dataset.code === code;
  });
}

function fillPendingMemberDiscount() {
  var code = getPendingMemberDiscount();
  if (!code) return;

  if (isDiscountCodeShownAsApplied(code)) {
    setPendingMemberDiscount('');
    return;
  }
  if (!isMemberDiscountOffered(code)) return;

  document.querySelectorAll('.cart-discount').forEach(function (root) {
    var idleEl = root.querySelector('.js-discount-idle');
    var editEl = root.querySelector('.js-discount-edit');
    var inputEl = root.querySelector('.js-discount-input');
    var statusEl = root.querySelector('.js-discount-status');
    if (!inputEl) return;
    if (idleEl) idleEl.hidden = true;
    if (editEl) editEl.hidden = false;
    if (statusEl) statusEl.hidden = true;
    inputEl.value = code;
  });
}

function showMemberDiscountCopied(button) {
  var block = button.closest('.member-discount');
  var statusEl = block ? block.querySelector('.js-member-discount-status') : null;

  button.classList.add('is-copied');
  if (statusEl) statusEl.textContent = button.dataset.copiedText;

  clearTimeout(button._memberDiscountTimer);
  button._memberDiscountTimer = setTimeout(function () {
    button.classList.remove('is-copied');
    if (statusEl) statusEl.textContent = '';
  }, MEMBER_DISCOUNT_COPIED_DURATION);
}

function copyMemberDiscountCode(button) {
  var code = button.dataset.code;
  if (!code) return;

  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(code)
      .then(function () { showMemberDiscountCopied(button); })
      .catch(function (error) { console.error('Error copying discount code:', error); });
  }

  setPendingMemberDiscount(code);
  fillPendingMemberDiscount();
}

/* ── delegated click handler (survives DOM refreshes) ────────── */

document.addEventListener('click', function (e) {

  /* Toggle cart drawer — never on the cart page itself: the shopper is already
     looking at their cart, so the header cart button is a no-op there. */
  var cartToggle = e.target.closest('.header-cart') || e.target.closest('.js-cart-toggle') || e.target.closest('.header-cell--cart');
  if (cartToggle) {
    e.preventDefault();
    if (isCartPage()) return;
    toggleCartDrawer();
    return;
  }

  /* Close cart drawer */
  if (e.target.closest('.overlay-box') || e.target.closest('.cart-drawer-close')) {
    e.preventDefault();
    closeCartDrawer();
    return;
  }

  /* Click outside to close cart drawer */
  if (document.body.classList.contains('open-drawer') || document.body.classList.contains('open-cart')) {
    var drawer = document.querySelector('.cart-sidebar');
    if (drawer && !drawer.contains(e.target) && !e.target.closest('.header-cart') && !e.target.closest('.js-cart-toggle') && !e.target.closest('.header-cell--cart')) {
      closeCartDrawer();
    }
  }

  /* Remove line item */
  var removeBtn = e.target.closest('.item-remove');
  if (removeBtn) {
    e.preventDefault();
    updateCartItemQuantity(removeBtn.dataset.line, 0);
    return;
  }

  var shadeDot = e.target.closest('.js-drawer-shade-dot');
  if (shadeDot) {
    e.preventDefault();
    if (shadeDot.disabled || shadeDot.classList.contains('is-sold-out')) return;
    var shadeCard = shadeDot.closest('.cart-drawer-product-card');
    if (shadeCard) updateDrawerCardVariant(shadeCard, shadeDot.dataset.variantId);
    return;
  }

  /* Upsell add-to-cart — both the drawer's own upsell cards and the PDP
     upsell block. Clicks from outside the drawer open it once the cart
     sections have re-rendered, matching every other add-to-cart path. */
  var upsellBtn = e.target.closest('.js-upsell-add-to-cart');
  if (upsellBtn) {
    e.preventDefault();
    if (upsellBtn.disabled) return;

    var variantId = upsellBtn.dataset.variantId;
    if (!variantId) return;

    var fromDrawer = !!upsellBtn.closest('.cart-sidebar');
    upsellBtn.classList.add('loading');
    upsellBtn.disabled = true;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (d) { throw d; });
        return r.json();
      })
      .then(function () {
        return refreshCart().then(function () {
          if (!fromDrawer) openCartDrawer();
        });
      })
      .catch(function (err) {
        console.error('Upsell add to cart error:', err);
        if (err && err.description) alert(err.description);
      })
      .finally(function () {
        upsellBtn.classList.remove('loading');
        upsellBtn.disabled = false;
      });
    return;
  }

  if (e.target.closest('.js-cart-points-link')) {
    closeCartDrawer(false);
    return;
  }

  var memberDiscountCopy = e.target.closest('.js-member-discount-copy');
  if (memberDiscountCopy) {
    e.preventDefault();
    copyMemberDiscountCode(memberDiscountCopy);
    return;
  }

  /* "Add a code" — the row's content is replaced by the discount form
     (same row, not an extra one) */
  var addCodeLink = e.target.closest('.js-discount-add-link');
  if (addCodeLink) {
    e.preventDefault();
    var addRoot = addCodeLink.closest('.cart-discount') || document;
    var idleEl = addRoot.querySelector('.js-discount-idle');
    var editEl = addRoot.querySelector('.js-discount-edit');
    var inputToFocus = addRoot.querySelector('.js-discount-input');
    if (idleEl) idleEl.hidden = true;
    if (editEl) editEl.hidden = false;
    if (inputToFocus) inputToFocus.focus();
    return;
  }

  /* Apply discount code */
  var applyLink = e.target.closest('.js-discount-apply-link');
  if (applyLink) {
    if (applyLink.disabled) return;
    var applyRoot = applyLink.closest('.cart-discount') || document;
    var inputEl = applyRoot.querySelector('.js-discount-input');
    var statusEl = applyRoot.querySelector('.js-discount-status');
    var code2 = inputEl ? inputEl.value.trim() : '';
    if (statusEl) statusEl.hidden = true;

    if (code2 && code2.length) {
      applyDiscountCode(code2, applyRoot);
    } else if (inputEl) {
      inputEl.focus();
    }
    return;
  }

  /* Remove discount code */
  var removeDiscount = e.target.closest('.remove-discount');
  if (removeDiscount) {
    removeDiscountCode(removeDiscount.dataset.code);
    return;
  }
});

/* Submit a discount code with Enter from the discount input */
document.addEventListener('keydown', function (e) {
  if (e.key !== 'Enter') return;
  if (!e.target.classList || !e.target.classList.contains('js-discount-input')) return;
  e.preventDefault();

  var enterRoot = e.target.closest('.cart-discount') || document;
  var enterApplyBtn = enterRoot.querySelector('.js-discount-apply-link');
  if (enterApplyBtn && enterApplyBtn.disabled) return;

  var statusEl = enterRoot.querySelector('.js-discount-status');
  var code = e.target.value.trim();
  if (statusEl) statusEl.hidden = true;
  if (code && code.length) applyDiscountCode(code, enterRoot);
});

document.addEventListener('input', function (e) {
  if (!e.target.classList || !e.target.classList.contains('js-discount-input')) return;
  var pending = getPendingMemberDiscount();
  if (pending && e.target.value.trim().toLowerCase() !== pending.toLowerCase()) {
    setPendingMemberDiscount('');
  }
});

/* ── delegated change handler: quantity dropdown ─────────────────
   Matches any cart-rendering section (drawer + cart page), since both use the
   shared cart-line-item snippet. */

document.addEventListener('change', function (e) {
  var select = e.target.closest('.qty-select');
  if (!select || !select.closest('[data-cart-section-id]')) return;
  updateCartItemQuantity(select.dataset.line, parseInt(select.value, 10) || 1);
});

/* ── keyboard: Escape to close + focus trap ──────────────────── */

document.addEventListener('keydown', function (e) {
  if (e.key === 'Escape' && document.body.classList.contains('open-drawer')) {
    closeCartDrawer();
    return;
  }

  /* Focus trap inside the drawer */
  if (e.key === 'Tab' && document.body.classList.contains('open-drawer')) {
    var drawer = document.querySelector('.cart-sidebar');
    if (!drawer) return;

    var focusable = Array.prototype.filter.call(
      drawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ),
      function (el) { return el.offsetParent !== null && !el.closest('[inert]'); }
    );
    if (!focusable.length) return;

    var first = focusable[0];
    var last  = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first || document.activeElement === drawer) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }
});

/* ── initialise ──────────────────────────────────────────────── */

updateCartCount();
initSplide();
initCompleteBagSplide();
loadCartRecommendations();
fillPendingMemberDiscount();

if (typeof Splide === 'undefined') {
  window.addEventListener('load', function () {
    initSplide();
    initCompleteBagSplide();
  });
}

/* The cart can arrive at this page already qualifying (added in another tab, a
   recovered cart, a cached page), so reconcile the gift once on load. */
syncFreeGift().then(function (changed) {
  if (changed) refreshCart(1);
});
